/**
 * SectionCard / CompactLabel - Auxiliares de leiaute "Sidebar produtiva"
 *
 * Extraídos do WorkflowInspector para que os modais do módulo Workflow
 * (criação e configurações do processo) usem exatamente o mesmo padrão
 * visual do sheet do construtor: cartão de seção com título em caixa alta,
 * botão "?" para as explicações e rótulos compactos.
 *
 * Camada de apresentação apenas — nenhuma regra de negócio aqui.
 *
 * @version 1.0.0
 * RELEASE: 2026-08-31 v1.0.0 - Extração do WorkflowInspector para reuso nos modais
 */

import type React from 'react';
import { Label } from '@/components/ui/label';
import { cn } from '@/lib/utils';
import { Ajuda } from './Ajuda';

/** Cartão de seção com título em caixa alta, usado para agrupar campos
 *  relacionados e reduzir a aparência de formulário longo.
 *  `help` exibe a explicação no botão "?" ao lado do título (evita
 *  parágrafos longos que ocupam espaço vertical). */
export function SectionCard({
  title,
  help,
  highlight,
  children,
  className,
}: {
  title?: string;
  /** Texto explicativo exibido no tooltip "?" ao lado do título */
  help?: string;
  highlight?: 'default' | 'alert';
  children: React.ReactNode;
  className?: string;
}) {
  return (
    <section
      className={cn(
        'rounded-xl border p-3 space-y-3',
        highlight === 'alert' ? 'bg-destructive/5 border-destructive/20' : 'bg-muted/30 border-border/60',
        className,
      )}
    >
      {title && (
        <div className="flex items-center gap-2">
          <span className="text-[10px] font-bold uppercase tracking-widest text-muted-foreground/70">
            {title}
          </span>
          {help && <Ajuda texto={help} />}
          <div className="h-px flex-1 bg-border/60" />
        </div>
      )}
      {children}
    </section>
  );
}

/** Rótulo compacto para os campos do painel/modais */
export function CompactLabel({
  children,
  help,
  className,
}: {
  children: React.ReactNode;
  help?: string;
  className?: string;
}) {
  return (
    <Label className={cn('flex items-center gap-1 text-[11px] font-semibold text-muted-foreground', className)}>
      {children}
      {help && <Ajuda texto={help} />}
    </Label>
  );
}
